/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { IPortalProps, Portal } from "../Portal";
import { overrideForwardRef } from "../OverridableComponent";
import { useEditorRootEl } from "../../../hooks";
import { IPopperBaseProps, PopperBase } from "../PopperBase";

import "./Popper.scss";

export interface IPopperProps extends IPopperBaseProps {
	children?: React.ReactNode;
	container?: IPortalProps["container"];
	disablePortal?: IPortalProps["disablePortal"];
	keepMounted?: boolean;
	open: boolean;
	className?: string;
}

export const Popper = overrideForwardRef<IPopperProps, "div">(
	(
		{
			Component,
			className,
			children,
			container: containerProp,
			disablePortal,
			keepMounted,
			open,
			...props
		},
		ref
	) => {
		const baseClass = BEM("edt-popper");

		const editorRootRef = useEditorRootEl();
		const container: IPortalProps["container"] = containerProp || editorRootRef?.current;

		if (className) {
			baseClass.add(className);
		}

		if (!keepMounted && !open) {
			return null;
		}

		return (
			<Portal container={container} disablePortal={disablePortal}>
				<PopperBase
					className={baseClass.toClassName()}
					Component={Component}
					ref={ref}
					open={open}
					{...props}
				>
					{children}
				</PopperBase>
			</Portal>
		);
	}
);

Popper.defaultProps = {
	Component: "div",
	placement: "bottom",
	safePadding: 5
};
